import { Component, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeycloakAdminService } from '../../../services/keycloak/keycloak.service';
import { AuthLayoutComponent } from '../../ui/auth-layout/auth-layout.component';
import {
  DEFAULT_LANDING_URL,
  RETURN_URL_PARAM,
  SIGN_IN_REASON_PARAM,
  SignInReason,
  isSignInReason,
  safeReturnUrl,
} from '../../../services/routing/return-url';
import { SignInError, describeSignInError } from './sign-in-errors';

@Component({
  selector: 'signin-form',
  standalone: true,
  templateUrl: './signinForm.component.html',
  imports: [ReactiveFormsModule, RouterLink, AuthLayoutComponent],
})
export class SigninForm {
  router = inject(Router);
  route = inject(ActivatedRoute);
  keycloakService = inject(KeycloakAdminService);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  loading: boolean = false;
  showPassword: boolean = false;
  capsLockOn: boolean = false;
  /** Set on the first submit, so empty fields only complain after a try. */
  submitted: boolean = false;
  /**
   * Stays until the next submit (it used to vanish after 5 s). Clearing it on
   * the first keystroke would collapse the note above the form and shift the
   * fields under the caret.
   */
  error: SignInError | null = null;

  /** Why sign-in is showing (session expired / signed out), from ?reason. */
  reason: SignInReason | null = null;
  /** Where to go after signing in: a validated ?returnUrl or the dashboard. */
  returnUrl: string = DEFAULT_LANDING_URL;

  signinForm = new FormGroup({
    // Accepts a username as well as an email, so no email validator.
    email: new FormControl('', [Validators.required]),
    password: new FormControl('', Validators.required),
  });

  constructor() {
    // A guard can send the user back here while sign-in is already on screen
    // (same route, so this form is reused): follow the query params rather
    // than a one-time snapshot.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed())
      .subscribe((params) => {
        const reason = params.get(SIGN_IN_REASON_PARAM);
        this.reason = isSignInReason(reason) ? reason : null;
        this.returnUrl = safeReturnUrl(params.get(RETURN_URL_PARAM));
      });
  }

  // get email form control for validation
  get email() {
    return this.signinForm.get('email');
  }

  get password() {
    return this.signinForm.get('password');
  }

  get showEmailError(): boolean {
    const control = this.email;
    return !!control?.invalid && ((control.dirty && control.touched) || this.submitted);
  }

  get showPasswordError(): boolean {
    const control = this.password;
    return !!control?.invalid && ((control.dirty && control.touched) || this.submitted);
  }

  /** The session notice, or null. Hidden once an error takes its place. */
  get notice(): { title: string; message: string } | null {
    if (!this.reason || this.error) return null;
    const goingBack = this.returnUrl !== DEFAULT_LANDING_URL;
    if (this.reason === 'expired') {
      return {
        title: 'Your session expired',
        message: goingBack
          ? 'Sign in again to go back to the page you were on.'
          : 'Sign in again to carry on.',
      };
    }
    return {
      title: 'You were signed out',
      message: goingBack
        ? 'Your session was no longer accepted. Sign in again to go back to the page you were on.'
        : 'Your session was no longer accepted. Sign in again to carry on.',
    };
  }

  // Toggle password visibility
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  /** Any key on the password field reports the Caps Lock state. */
  checkCapsLock(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (typeof keyboardEvent.getModifierState === 'function') {
      this.capsLockOn = keyboardEvent.getModifierState('CapsLock');
    }
  }

  // submit the form
  async handleSubmit() {
    if (this.loading) return;
    this.submitted = true;

    // Usernames and emails never start or end with a space; pasted ones often do.
    const username = (this.signinForm.value.email ?? '').trim();
    if (username !== this.signinForm.value.email) {
      this.email?.setValue(username);
    }

    if (this.signinForm.invalid) {
      this.signinForm.markAllAsTouched();
      this.focusField(this.email?.invalid ? 'signin-email' : 'signin-password');
      return;
    }

    this.loading = true;
    this.error = null;
    try {
      await this.keycloakService.authenticate(
        username,
        this.signinForm.value.password!
      );
      // A guard may redirect back to sign-in (and the promise still resolves
      // true), so check where the router ended up, not the result.
      this.router
        .navigateByUrl(this.returnUrl)
        .then(() => {
          const path = this.router.url.split(/[?#]/)[0];
          if (!/^\/organization(?:\/|$)/.test(path)) this.loading = false;
        })
        .catch(() => (this.loading = false));
    } catch (error: any) {
      const online =
        typeof navigator === 'undefined' || navigator.onLine !== false;
      this.error = describeSignInError(error, online);
      this.loading = false;
      if (this.error.field === 'password') {
        this.focusField('signin-password', true);
      }
    }
  }

  private focusField(id: string, select = false) {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => {
      const field = this.document.getElementById(id) as HTMLInputElement | null;
      field?.focus();
      if (select) field?.select();
    });
  }
}
